/**
 * Phase L — ThemeProvider (Track C revision).
 *
 * Single source of truth for the app's light/dark theme. Wraps the
 * entire app tree so every component can read + set the theme via
 * `useTheme()`.
 *
 * State shape:
 *   {
 *     mode:      "auto" | "light" | "dark",   // user's preference
 *     effective: "light" | "dark",            // resolved theme applied
 *     isAutoDark: boolean,                    // clock says night when mode=auto
 *     isOsDark:   boolean | null,             // OS prefers-color-scheme,
 *                                             //   null when matchMedia unavailable
 *     setMode(mode): void,                    // action
 *   }
 *
 * Auto-mode resolution (Track C — 2026-07 dispatch, "GO — human
 * confirmed A4 + B1 + C1"):
 *
 *   1. Explicit override — `localStorage["pooplucker_theme_mode"]`
 *      is `"light"` or `"dark"` → USE THAT. OS `change` events are
 *      IGNORED while an explicit override is set.
 *   2. `mode === "auto"` (default; also the value for absent /
 *      invalid localStorage) → read
 *      `window.matchMedia("(prefers-color-scheme: dark)")` and
 *      follow the OS. Live-track `mql.change` events.
 *   3. `matchMedia` unavailable / returns non-boolean `.matches`
 *      (SSR, ancient browsers) → fall back to the local-clock
 *      auto-dark window (7pm-7am), preserved verbatim from the
 *      pre-Track C behavior.
 *
 * Family L exception:
 *   The literal string `prefers-color-scheme` is banned everywhere
 *   in `frontend/src/**` by `meta.family-l-guard.test.js`, EXCEPT
 *   for one narrow line-range window in THIS file (the constant
 *   `OS_DARK_QUERY` below) and one in `public/index.html` (the
 *   pre-first-paint FOWT boot script). Both allowlist entries are
 *   dead-allowlist-checked so a future refactor that removes the
 *   term here will also fail the guard until the exception is
 *   removed.
 *
 * FOWT (Flash-of-Wrong-Theme):
 *   The pre-first-paint inline script in `public/index.html`
 *   resolves the theme using the SAME precedence order and writes
 *   `data-theme` to `<html>` before bundled JS loads. This provider
 *   initialises its `effective` state from the already-set
 *   attribute so React's first render is byte-identical to the
 *   pre-paint pixels — no flip on mount.
 *
 * Persistence:
 *   `localStorage["pooplucker_theme_mode"]` stores the user's `mode`.
 *   Falls back to a module-scoped in-memory variable if localStorage
 *   throws (private browsing, disabled storage, cookies-off, etc.).
 *   NEVER let localStorage failure break the component.
 *
 * Clock re-evaluation:
 *   When BOTH `matchMedia` is unavailable AND `mode === "auto"`, the
 *   clock heuristic drives the theme; we schedule a setTimeout for
 *   the next 7am/7pm crossing so the transition happens without a
 *   full reload. In OS-pref-driven auto mode the timer is not
 *   needed (the `change` event handles transitions).
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import {
  NIGHT_START_HOUR,
  NIGHT_END_HOUR,
  isAutoDarkAt,
  msUntilNextCrossing,
} from "./nightModeBoundary";

export const THEME_STORAGE_KEY = "pooplucker_theme_mode";
// Re-exported for Phase L test compatibility. The canonical
// declaration lives in `nightModeBoundary.js` (Phase L.1.1 refactor).
export { NIGHT_START_HOUR as DARK_HOUR_START, NIGHT_END_HOUR as DARK_HOUR_END };
export { isAutoDarkAt, msUntilNextCrossing };

// Track C — the ONLY runtime OS-preference read query string in the
// entire src tree. Family-L-guard allowlist references this exact
// constant declaration; do not inline the string elsewhere and do
// not remove the const without also updating the allowlist range.
const OS_DARK_QUERY = "(prefers-color-scheme: dark)";

// In-memory fallback for when localStorage is unavailable.
let _inMemoryMode = null;

function _safeReadStorage() {
  try {
    const v = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (v === "auto" || v === "light" || v === "dark") return v;
    return null;
  } catch (_) {
    return _inMemoryMode;
  }
}

function _safeWriteStorage(mode) {
  _inMemoryMode = mode;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch (_) {
    /* silenced — in-memory fallback already set above */
  }
}

/**
 * Read OS dark-mode preference at the current moment.
 * Returns `true` (OS dark), `false` (OS light), or `null`
 * (`matchMedia` unavailable — SSR or ancient browser). Callers must
 * be tolerant of `null` and route to the clock-fallback branch.
 *
 * Wrapped in try/catch because some very old / stubbed matchMedia
 * shims throw synchronously on unknown media queries.
 */
function _readOsDark() {
  try {
    if (typeof window === "undefined" || !window.matchMedia) return null;
    const mql = window.matchMedia(OS_DARK_QUERY);
    if (!mql || typeof mql.matches !== "boolean") return null;
    return mql.matches;
  } catch (_) {
    return null;
  }
}

/**
 * Read the theme already resolved onto `<html>` by the pre-paint
 * FOWT boot script. Used as the ThemeProvider's initial `effective`
 * so React's first render matches the pre-paint pixels exactly
 * (no flip on mount). Returns `null` when the attribute is missing
 * or invalid; caller must supply its own fallback.
 */
function _readBootResolvedEffective() {
  try {
    if (typeof document === "undefined") return null;
    const attr = document.documentElement.getAttribute("data-theme");
    if (attr === "light" || attr === "dark") return attr;
  } catch (_) { /* SSR or exotic environment */ }
  return null;
}

const ThemeContext = createContext({
  mode: "auto",
  effective: "light",
  isAutoDark: false,
  isOsDark: null,
  setMode: () => {},
});

export function ThemeProvider({ children }) {
  const [mode, _setMode] = useState(() => _safeReadStorage() || "auto");
  const [isAutoDark, setIsAutoDark] = useState(() =>
    isAutoDarkAt(new Date()),
  );
  const [isOsDark, setIsOsDark] = useState(() => _readOsDark());

  const effective = useMemo(() => {
    // Explicit override wins.
    if (mode === "light") return "light";
    if (mode === "dark") return "dark";
    // Auto mode. If we have an OS-pref reading, honor it.
    if (isOsDark === true) return "dark";
    if (isOsDark === false) return "light";
    // Otherwise fall back to the clock heuristic (matchMedia
    // unavailable — SSR / ancient browser / stubbed shim).
    return isAutoDark ? "dark" : "light";
  }, [mode, isOsDark, isAutoDark]);

  const setMode = useCallback((next) => {
    if (next !== "auto" && next !== "light" && next !== "dark") return;
    _safeWriteStorage(next);
    _setMode(next);
  }, []);

  // Apply data-theme on <html> so CSS variables cascade. The boot
  // script already set this pre-paint — the effect below is a
  // no-op on initial mount when the resolutions agree.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("data-theme", effective);
  }, [effective]);

  // Runtime OS-preference listener — only attached in auto mode
  // (explicit overrides must ignore OS `change` events per the
  // Track C locked precedence). Cleaned up on unmount or when the
  // user pins an explicit mode.
  useEffect(() => {
    if (mode !== "auto") return undefined;
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    let mql;
    try {
      mql = window.matchMedia(OS_DARK_QUERY);
    } catch (_) {
      return undefined;
    }
    if (!mql) return undefined;
    const onChange = (e) => setIsOsDark(!!e.matches);
    // Re-sync current value on subscribe — the OS may have changed
    // between initial useState() and this effect firing.
    if (typeof mql.matches === "boolean") setIsOsDark(mql.matches);
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    // Safari < 14 fallback — deprecated `addListener` API.
    if (typeof mql.addListener === "function") {
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    }
    return undefined;
  }, [mode]);

  // Clock re-evaluation — only meaningful when we're in auto mode
  // AND we don't have an OS-pref signal. In OS-pref-driven auto
  // mode the `change` event handles transitions and the clock is
  // irrelevant. Preserved for the ancient-browser / SSR path.
  useEffect(() => {
    setIsAutoDark(isAutoDarkAt(new Date()));
    if (mode !== "auto") return undefined;
    if (isOsDark !== null) return undefined; // OS pref beats clock
    const ms = msUntilNextCrossing(new Date());
    const t = setTimeout(() => setIsAutoDark(isAutoDarkAt(new Date())), ms);
    return () => clearTimeout(t);
  }, [mode, isOsDark]);

  const value = useMemo(
    () => ({ mode, effective, isAutoDark, isOsDark, setMode }),
    [mode, effective, isAutoDark, isOsDark, setMode],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

// Exposed for testing — allows precedence tests to seed initial
// state exactly the way the boot script would have, without
// coupling tests to jsdom attribute quirks.
export const __test_readBootResolvedEffective = _readBootResolvedEffective;
export const __test_readOsDark = _readOsDark;
export const __test_OS_DARK_QUERY = OS_DARK_QUERY;
